// External libraries
import * as React from "react";
import { observer } from "mobx-react";
import { QueryRoute } from "bernie-core";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { useLocalization } from "@shared-ui/localization-context";
import { withStores } from "stores";

// UITK
import { UitkLinkButton } from "uitk-react-button";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { UitkText } from "uitk-react-text";

// Types
import { ModuleMapProps } from "./typings";
import { ClickAction } from "./MapsEnums";
import { MapsFlexModuleResult } from "typings/microserviceModels/maps-flex-module";

// Components
import { useFullscreenOpen } from "./hooks";
import { MapContext } from "./MapContext";

const ModuleMap = React.lazy(() => import(/* webpackChunkName: "module-map" */ "./ModuleMap"));

/**
 * Wrapper Map Component. Here we inject the stores and is rendered the call to action button
 * and is rendered the Main Map Component.
 * @param {MapsProps} props
 */
export const Maps = withStores(
  "analytics",
  "routeStore",
  "hotels",
  "staticMap",
  "poi",
  "flexModuleModelStore"
)(
  observer((props: ModuleMapProps) => {
    const { sendUserToSRP, toggleShowFullscreenMap } = useFullscreenOpen(props);
    const { formatText } = useLocalization();
    const [isClient, setIsClient] = React.useState(false);

    React.useEffect(() => {
      setIsClient(true);
    }, []);

    const { flexTemplateNode, context, hotels: hotelsStore, staticMap, flexModuleModelStore } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as MapsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const shouldOpenMapInSRP = model.clickAction === ClickAction.SEND_TO_SRP;
    const mapCTAClickAction = shouldOpenMapInSRP ? sendUserToSRP : toggleShowFullscreenMap;

    const location = model.location ?? props.context.searchContext.location;
    const ctaText = isVariantEnabled(context, Experiment.Blossom_SRP_Lite_V2)
      ? formatText("maps.callToActionButtonViewInAMap")
      : formatText("maps.callToActionButtonShowMap");

    return (
      <div
        className="Maps uitk-cell all-cell-1-1 uitk-cell all-y-padding-three"
        id={id}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        <UitkLinkButton
          onClick={mapCTAClickAction}
          disabled={!hotelsStore.allHotelsOnMap.length}
          className="uitk-grid uitk-cell uitk-map-call-to-action"
        >
          <UitkCardAloha>
            <UitkFigure>
              <UitkImage
                alt={formatText("maps.hotel.multipleHotels.a11yLabel", location?.name)}
                src={staticMap.signedUrl}
              />
            </UitkFigure>
            <UitkCardAlohaContentSection>
              <UitkText size={300} className="uitk-type-center uitk-button-old-link">
                {ctaText}
              </UitkText>
            </UitkCardAlohaContentSection>
          </UitkCardAloha>
        </UitkLinkButton>
        <MapContext.Provider
          value={{
            toggleShowFullscreenMap,
            sendUserToSRP,
            shouldOpenMapInSRP,
          }}
        >
          <QueryRoute query={{ pwaDialogMap: `open-${id}` }}>
            {isClient && (
              <React.Suspense fallback={<div />}>
                <ModuleMap {...props} />
              </React.Suspense>
            )}
          </QueryRoute>
        </MapContext.Provider>
      </div>
    );
  })
);

Maps.displayName = "Maps";

export default Maps;
